import { get, difference } from 'lodash-es'
import qs from 'query-string'
import { decamelize } from 'humps'
import { FETCH_STATUSES, NEW_FLOW_MODAL_PATHS, FULL_PAGE_LAYOUT_PATHS } from '../../constants/flow'
import { REPLACE_CARD_REASONS } from '@/constants/card'
import { shouldUseBdmcManageCancelFlowModal } from '@/screens/cancel-card/cancelCardRouting'
import { appendClassName, removeClassName, getProductQueryParam } from './utils'
import { VeniceHandlerName, shouldTriggerVeniceCallback, WEBVIEW_CALLBACK_NAMES } from './mobile'
import { serverData, isModalView } from './server-data'
import { getQueryParams, getQueryParam } from './query'
import { CLIENT_ROUTE_PATHS } from './routes'
import { isReturnUriBackToReplaceCardFlow } from './screens/change-address'
import { isShippingAddressType, ADDRESS_TYPES } from 'universal/address'
import { FLOWS } from 'universal/ucs'
import { INTENT_TYPES } from 'universal/constants'
import { PRODUCT_NAMES } from 'universal/cfs-card'

const { CHANGE_ADDRESS, ADD_ADDRESS, REPLACE_CARD, SHOW_CARD, SET_PIN } = FLOWS
const { BUSINESS_DEBIT_CARD, CONSUMER_DEBIT_CARD } = PRODUCT_NAMES

// ============================
// New flow utils and constants
// ============================

const { SUCCESS } = FETCH_STATUSES

/**
 * Returns true for v2 Flows
 */
export function isNewFlowModal() {
  return window.PAYPAL?._newFlowModal
}

/**
 * Returns true for v1 Flows
 */
export function isOldFlowModal() {
  return isModalView() && !isNewFlowModal()
}

export function isFullPageLayout(location) {
  return FULL_PAGE_LAYOUT_PATHS.some((path) => location.includes(path))
}

export function setNewFlowModal(location) {
  const isNewFlowModalPath =
    NEW_FLOW_MODAL_PATHS.some((path) => location.includes(path)) ||
    shouldUseBdmcManageCancelFlowModal(location)
  if (isNewFlowModalPath) {
    removeClassName('root', 'vx_modal-wrapper')
    removeClassName('modal', 'vx_modal-flow')
    if (window.PAYPAL) {
      window.PAYPAL._newFlowModal = true
    }
  } else {
    appendClassName('root', 'vx_modal-wrapper')
    appendClassName('modal', 'vx_modal-flow')
    if (window.PAYPAL) {
      window.PAYPAL._newFlowModal = false
    }
  }
}

export function getFetchState(state, actionName) {
  return get(state, `ui.fetches[${actionName}]`) || {}
}

export function isSuccessfulFetch(state, actionName) {
  const { status } = getFetchState(state, actionName)
  return status === SUCCESS
}

export function getExistingCardAndAddressesState(state, cardId) {
  const card = (state.card?.cards || {})[cardId]
  const addresses = state.address?.addresses
  return { card, addresses }
}

export function generateQueryParamsByFlowName(flowName, returnUri) {
  if (shouldTriggerVeniceCallback()) {
    const webviewCallback = WEBVIEW_CALLBACK_NAMES[flowName] || VeniceHandlerName.close

    return {
      webviewCallback,
    }
  }

  return {
    returnUri,
  }
}

function getRequiredFlowInputParamNamesByFlowName(flowName = '') {
  const mandatoryParameters = ['productName']

  const { addressType = '', returnUri = '' } = getQueryParams() as any

  switch (flowName) {
    case CHANGE_ADDRESS:
      mandatoryParameters.push('addressType')
      if (isReturnUriBackToReplaceCardFlow(returnUri) && isShippingAddressType(addressType)) {
        mandatoryParameters.push('addressId')
      }
      break
    case ADD_ADDRESS:
      mandatoryParameters.push('addressType')
      break
    default:
  }

  if (serverData.isMobileNative) {
    mandatoryParameters.push('webviewCallback')
    return mandatoryParameters
  }

  return mandatoryParameters
}

function formatFlowInputParamErrorMessage(params = []) {
  if (params.length > 0) {
    return `${params.join(', ')} ${params.length === 1 ? 'is' : 'are'} missing`
  }

  return ''
}

export function addErrorMessageForMissingFlowInputParams(flowName) {
  const requiredFlowInputParams = getRequiredFlowInputParamNamesByFlowName(flowName)
  const missingParameters = difference(requiredFlowInputParams, Object.keys(getQueryParams()))
  const requiredFlowInputParamErrorMessage = formatFlowInputParamErrorMessage(missingParameters)

  if (requiredFlowInputParamErrorMessage) {
    return {
      errorType: 'MissingQueryParams',
      errorMessage: requiredFlowInputParamErrorMessage,
    }
  }
  return {}
}

export function createReplaceCardFlowUrl({ cardId = '', productName = '' }, reason, returnUri) {
  if (!cardId || !productName) {
    return ''
  }

  const queryString = qs.stringify({
    productName: getProductQueryParam(productName),
    reason,
    ...(generateQueryParamsByFlowName as any)(REPLACE_CARD),
    returnUri,
    directEntry: reason ? true : false,
  })

  return CLIENT_ROUTE_PATHS.REPLACE_CARD.replace(':cardId', cardId) + `?${queryString}`
}

export function createShowCardFlowUrl({ cardId, returnUri }) {
  if (!cardId) {
    return ''
  }

  const queryString = qs.stringify({
    ...(generateQueryParamsByFlowName as any)(SHOW_CARD),
    returnUri,
  })

  return CLIENT_ROUTE_PATHS.SHOW_CARD.replace(':cardId', cardId) + `?${queryString}`
}

export function createEditFundingFlowUrl({ cardId, returnUri, queryParams = {}, entryPoint }) {
  if (!cardId) {
    return ''
  }

  const queryString = qs.stringify({
    returnUri,
    entrypoint: entryPoint,
    ...queryParams,
  })

  return CLIENT_ROUTE_PATHS.FUNDING.replace(':cardId', cardId) + `?${queryString}`
}

export function createSetupFIFlowUrl({ productName = '', intent, variant, returnUri, cancelUri }) {
  if (!productName) {
    return ''
  }
  const queryString = qs.stringify({
    productName,
    intent,
    variant,
    returnUri,
    cancelUri,
  })

  return `${CLIENT_ROUTE_PATHS.SETUP_FUNDING}?${queryString}`
}

export function createChangeAddressFlowUrl({
  cardId,
  productName = '',
  addressType = '',
  addressId,
  intent,
  variant,
  returnUri,
  cancelUri,
  skipAddressSelection,
  reason,
}) {
  if (!productName || !addressType) {
    return ''
  }

  const queryString = qs.stringify({
    cardId,
    productName,
    addressType,
    addressId,
    intent,
    variant,
    returnUri,
    cancelUri,
    skipAddressSelection,
    reason,
  })

  return `${CLIENT_ROUTE_PATHS.CHANGE_ADDRESS}?${queryString}`
}

export function createSetPinFlowUrl({ cardId = '', productName = '' }, additionalParams) {
  if (!cardId || !productName) {
    return ''
  }

  const queryString = qs.stringify({
    productName: getProductQueryParam(productName),
    ...(generateQueryParamsByFlowName as any)(SET_PIN),
    ...additionalParams,
  })

  return CLIENT_ROUTE_PATHS.SET_PIN.replace(':cardId', cardId) + `?${queryString}`
}

export function createAddAddressFlowUrl(
  { productName, addressType = ADDRESS_TYPES.SHIPPING },
  returnUri,
  cancelUri,
) {
  if (!productName) {
    return ''
  }

  const queryString = qs.stringify({
    addressType,
    productName: getProductQueryParam(productName),
    returnUri,
    cancelUri,
  })

  return CLIENT_ROUTE_PATHS.ADD_ADDRESS + `?${queryString}`
}

export function enableDynamicFirstStepOption() {
  return {
    allowFirstStepUpdate: true,
  }
}

export function isFlowIntentForEnrollment() {
  const intent = getQueryParam('intent')
  const action = getQueryParam('action')
  const entrypoint = getQueryParam('entrypoint')
  return (
    intent === INTENT_TYPES.ENROLL ||
    action === 'upgrade' ||
    entrypoint === 'cancel_add_fi' ||
    entrypoint === 'add_fi'
  )
}

export const isPayrollPartnerVariantOrIntent = () => {
  const { intent, variant } = getQueryParams() as any

  return intent === INTENT_TYPES.PAYROLL_PARTNER || variant === INTENT_TYPES.PAYROLL_PARTNER
}

export function getFormattedFlowName(subFlowName = '', flowName = '') {
  const ucsPrefix = 'UCS_'
  const flowNamePrefix = flowName ? decamelize(flowName).toUpperCase() : 'FLOW'
  const flowNameSuffix = subFlowName ? `_${decamelize(subFlowName).toUpperCase()}` : ''
  return `${ucsPrefix}${flowNamePrefix}${flowNameSuffix}`
}

export function isFlowForRequestPhysicalCard({ withFee }: { withFee?: boolean } = {}) {
  const requestWithFee = getQueryParam('intent') === INTENT_TYPES.REQUEST_PHYSICAL_CARD_WITH_FEE
  const requestWithoutFee =
    getQueryParam('reason') === REPLACE_CARD_REASONS.GET_PHYSICAL_CARD && !requestWithFee
  if (withFee === undefined) {
    return requestWithFee || requestWithoutFee
  }
  return withFee ? requestWithFee : requestWithoutFee
}

/** PPDC request-physical-card flows hide the FlowModal navbar title (legacy parity). */
export function shouldHidePpdcRequestPhysicalCardModalTitle(productName?: string) {
  const resolvedProductName = productName || getQueryParam('productName')
  return resolvedProductName === CONSUMER_DEBIT_CARD && isFlowForRequestPhysicalCard()
}

/**
 * Checks if the current flow is a debit card enrollment flow.
 * Uses productName parameter as the primary indicator, following the pattern
 * established by enrollCardFlowEligibilityCheck().
 *
 * @returns {boolean} True if this is a debit card enrollment flow
 */
export function isDebitCardEnrollmentFlow() {
  const productName = getQueryParam('productName')
  // Check for debit card product names - following the same pattern as enrollCardFlowEligibilityCheck
  return (
    productName &&
    (productName.includes('DEBIT_CARD') ||
      productName === 'CONSUMER_DEBIT_CARD' ||
      productName === 'BUSINESS_DEBIT_CARD')
  )
}

/**
 * Check if current route is an enrollment route
 *
 * @returns {boolean} True if the current path includes enrollment routes
 */
export const isEnrollmentRoute = () => {
  return window.location.pathname.includes('/enroll')
}

/** BDMC enroll uses legacy modal chrome without GB consumer-enroll layout tweaks. */
export function isBdmcEnrollRoute() {
  return window.location.pathname.includes('/business/enroll')
}

/**
 * Checks if the current context is a GB-specific consumer debit card enrollment flow.
 * This is the authoritative function for determining when to show GB-specific UI elements
 * like the PayPal logo overlay. Excludes BDMC (/business/enroll, BUSINESS_DEBIT_CARD).
 *
 * @returns {boolean} True if this is a GB consumer debit card enrollment flow
 */
export function isGBDebitCardEnrollmentFlow() {
  if (isBdmcEnrollRoute()) {
    return false
  }

  if (getQueryParam('productName') === BUSINESS_DEBIT_CARD) {
    return false
  }

  const isGBCountry = serverData?.locality?.country === 'GB' // eslint-disable-line i18n-scan/no-country-dependency -- GB-specific flow logic required for enrollment
  const isDebitCardFlow = isDebitCardEnrollmentFlow()
  const hasEnrollmentIntent = isFlowIntentForEnrollment()

  // Check if we're in enrollment context via URL path or entrypoint
  const entrypoint = getQueryParam('entrypoint')
  const enrollmentEntrypoints = ['add_fi', 'cancel_add_fi']
  const isEnrollmentEntrypoint = entrypoint && enrollmentEntrypoints.includes(entrypoint)

  // Consider it an enrollment flow if any of these are true:
  // 1. Has enrollment intent (action=upgrade, intent=enroll, etc.)
  // 2. Has enrollment entrypoint (add_fi, cancel_add_fi)
  // 3. URL path includes enrollment routes (/enroll, /create-pin)
  const isEnrollmentFlow =
    isDebitCardFlow && (hasEnrollmentIntent || isEnrollmentEntrypoint || isEnrollmentRoute())
  return isGBCountry && isEnrollmentFlow
}

/**
 * @deprecated Use isGBDebitCardEnrollmentFlow() instead.
 * This function is kept for backward compatibility but should not be used for new features.
 * It has limitations as it only works when intent=enroll is present.
 */
export function isGBFlowIntentForEnrollment() {
  return isFlowIntentForEnrollment() && serverData?.locality?.country === 'GB' // eslint-disable-line i18n-scan/no-country-dependency -- GB-specific flow logic required for enrollment
}
